import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell, TableSkeleton, Amount, ListEmptyRow, StatCard } from '@redpanda/ui';
import { useRefetchOnFocus } from '@redpanda/utils';
import { useReceivableStore, AGING_BUCKETS, CustomerReceivable } from '../stores/pmReceivableStore';
import { useCrmCustomers, customerName } from '../stores/lookupStore';
import { useDataChanged } from '../utils/dataEvents';

/**
 * 依客戶：帳齡分析＋平均收款天數（主流「依客戶帳齡表」的做法）
 * - 帳齡只算已請款、還沒收齊的款項，依過了預計收款日幾天分組；各列加總＝已請款未收總額（沒關聯客戶的歸「未指定客戶」，排最後）
 * - 平均收款天數＝請款日到收齊日；沒有請款日的收款不列入，另外標示筆數
 */
const ReceivableCustomersPanel: React.FC = () => {
  const { t } = useTranslation(['translation', 'pages']);
  const { customers: rows, customersFetchTime, fetchCustomers } = useReceivableStore();
  const customers = useCrmCustomers();

  useEffect(() => { fetchCustomers(); }, [fetchCustomers]);
  useDataChanged(['payments', 'projects', 'packages'], fetchCustomers);
  useRefetchOnFocus(fetchCustomers);

  const tp = (key: string, options?: Record<string, unknown>) => t(`pmReceivables.customers.${key}`, { ns: 'pages', ...options });
  const sum = (pick: (row: CustomerReceivable) => number) => rows.reduce((total, row) => total + pick(row), 0);
  const overdue = sum(row => row.invoicedOutstanding - row.aging.current);
  // 全部客戶的平均收款天數：依各客戶收齊筆數加權
  const paidCount = sum(row => row.paidCount);
  const weighted = (pick: (row: CustomerReceivable) => number | null) =>
    paidCount > 0 ? Math.round((sum(row => (pick(row) ?? 0) * row.paidCount) / paidCount) * 10) / 10 : null;
  const avgDaysToPay = weighted(row => row.avgDaysToPay);
  const days = (value: number | null) => (value === null ? '—' : tp('days', { count: value }));

  return (
    <>
      <div className="mb-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <StatCard title={tp('summary.invoicedOutstanding')} value={<Amount value={sum(row => row.invoicedOutstanding)} />} />
        <StatCard
          title={tp('summary.overdue')}
          value={<span className={overdue > 0 ? 'text-red-600 dark:text-red-400' : ''}><Amount value={overdue} /></span>}
        />
        <StatCard title={tp('avgDaysToPay')} value={days(avgDaysToPay)} hint={tp('paidCount', { count: paidCount })} />
      </div>

      <p className="mb-4 text-[13px] text-fg-secondary">{tp('hint')}</p>

      {customersFetchTime === null ? (
        <TableSkeleton rows={5} columns={8} />
      ) : (
        <div className="bg-surface shadow dark:shadow-none dark:border dark:border-line overflow-hidden rounded-lg">
          <div className="overflow-x-auto scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
            <Table style={{ tableLayout: 'fixed', minWidth: '1160px' }}>
              <TableHeader>
                <TableRow>
                  <TableHead>{tp('customer')}</TableHead>
                  {AGING_BUCKETS.map(bucket => (
                    <TableHead key={bucket} numeric className="w-32">{tp(`aging.${bucket}`)}</TableHead>
                  ))}
                  <TableHead numeric className="w-32">{tp('invoicedOutstanding')}</TableHead>
                  <TableHead numeric className="w-48">{tp('avgDaysToPay')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <ListEmptyRow colSpan={8} filtered={false} title={tp('empty')} />
                ) : (
                  <>
                    {rows.map(row => (
                      <TableRow key={row.customerId ?? 'unassigned'}>
                        <TableCell className="truncate">
                          {row.customerId ? customerName(customers, row.customerId) : <span className="text-fg-secondary">{tp('unassigned')}</span>}
                        </TableCell>
                        {AGING_BUCKETS.map(bucket => (
                          <TableCell
                            key={bucket}
                            numeric
                            className={row.aging[bucket] > 0 && bucket !== 'current' ? 'text-red-600 dark:text-red-400' : row.aging[bucket] > 0 ? '' : 'text-fg-muted'}
                          >
                            {row.aging[bucket] > 0 ? <Amount value={row.aging[bucket]} /> : '—'}
                          </TableCell>
                        ))}
                        <TableCell numeric className="font-semibold">
                          {row.invoicedOutstanding > 0 ? <Amount value={row.invoicedOutstanding} /> : <span className="font-normal text-fg-muted">—</span>}
                        </TableCell>
                        <TableCell numeric>
                          <div>{days(row.avgDaysToPay)}</div>
                          {row.paidCount > 0 && <div className="mt-0.5 text-[13px] text-fg-secondary">{tp('paidCount', { count: row.paidCount })}</div>}
                          {row.excludedCount > 0 && (
                            <div className="mt-0.5 text-[13px] text-fg-muted whitespace-normal">{tp('excluded', { count: row.excludedCount })}</div>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {/* 合計列：各帳齡區間加總（＝已請款未收總額） */}
                    <TableRow>
                      <TableCell className="font-semibold">{tp('total')}</TableCell>
                      {AGING_BUCKETS.map(bucket => (
                        <TableCell key={bucket} numeric className="font-semibold"><Amount value={sum(row => row.aging[bucket])} /></TableCell>
                      ))}
                      <TableCell numeric className="font-semibold"><Amount value={sum(row => row.invoicedOutstanding)} /></TableCell>
                      <TableCell numeric className="font-semibold">{days(avgDaysToPay)}</TableCell>
                    </TableRow>
                  </>
                )}
              </TableBody>
            </Table>
          </div>
        </div>
      )}
    </>
  );
};

export default ReceivableCustomersPanel;
